import { useRequiredContext } from "@redotech/react-util/context";
import { fgColumnEntries } from "@redotech/redo-fulfillment-common/views/fulfillment/fg-column-catalog";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoTablePaddedCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Link } from "@redotech/redo-web/link";
import { ColumnAlignment } from "@redotech/redo-web/table";
import { Column2 } from "@redotech/redo-web/table2";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { useParams } from "react-router-dom";
import { getOrderNavigationUrl } from "../../common/order-navigation-url";

const catalogEntry = fgColumnEntries["order-name"];

const getOrderNamesIds = (
  row: FulfillmentGroupData,
): Record<string, { id: string } | { redoOrderId: string }> => {
  return row.orders.reduce(
    (acc, order) => {
      if (order.name) {
        acc[order.name] = order.redoOrderId
          ? { redoOrderId: order.redoOrderId }
          : { id: order.id };
      }
      return acc;
    },
    {} as Record<string, { id: string } | { redoOrderId: string }>,
  );
};

export const createOrderNameColumn = ({
  sourceView,
}: { sourceView?: string } = {}): Column2<FulfillmentGroupData> => ({
  key: catalogEntry.key,
  title: catalogEntry.title,
  alignment: ColumnAlignment.LEFT,
  renderNormalCell: ({ row }) => {
    const orderNamesIds = getOrderNamesIds(row);
    const orderNameCell = (
      <OrderNameCell
        groupId={row._id}
        orderNamesIds={orderNamesIds}
        sourceView={sourceView}
      />
    );

    if (Object.values(orderNamesIds).length === 1) {
      return orderNameCell;
    }

    return (
      <Tooltip title={Object.keys(orderNamesIds).join(", ")}>
        <div>{orderNameCell}</div>
      </Tooltip>
    );
  },
  sort: catalogEntry.sortDirection,
  width: catalogEntry.defaultWidth,
});

export const OrderNameColumn: Column2<FulfillmentGroupData> =
  createOrderNameColumn();

export const OrderNameCell = memo(function OrderNameCell({
  orderNamesIds,
  groupId,
  sourceView,
}: {
  orderNamesIds: Record<string, { id: string } | { redoOrderId: string }>;
  groupId: string;
  sourceView?: string;
}) {
  const { viewName } = useParams<{ viewName: string }>();
  // OMS-3209: forward the source fulfillment view so the order detail page
  // can render the breadcrumb under Fulfillments instead of "All orders".
  const sourceViewName = sourceView ?? viewName;
  // Build URLs from `ordersOutboundLabelsHref` (flag-aware: `/shipping/...`
  // or legacy `/order-management/...`) so the link lands directly at the
  // right route tree instead of going through a `NavOrgRedirect` hop —
  // that hop briefly puts the URL outside every primary nav tab's
  // pathRoot, unmounting the side nav for a frame (visible as a flash).
  const { ordersOutboundLabelsHref } = useRequiredContext(
    MerchantRoutesContext,
  );

  return (
    <RedoTablePaddedCell>
      {Object.entries(orderNamesIds).map(([name, orderId], i) => {
        // Only non redo orders have an encoded order id due to irregularities in the order id format
        const link = getOrderNavigationUrl({
          fulfillmentGroupId: groupId,
          orderId,
          ordersOutboundLabelsHref,
          sourceView: sourceViewName,
        });
        return (
          <Link
            data-testid="fulfillment-table-order-name"
            key={i}
            onClick={(e) => e.stopPropagation()}
            to={link}
          >
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {name}
            </Text>
          </Link>
        );
      })}
    </RedoTablePaddedCell>
  );
});
